/**
 * Core FileUpload — Compound component with drag-and-drop, progress tracking,
 * retry/delete actions, and multiple visual styles.
 *
 * Follows the composable compound-component pattern:
 *   <FileUpload.Root>
 *     <FileUpload.DropZone onDropFiles={handleDrop} />
 *     <FileUpload.List>
 *       {files.map(f => <FileUpload.ListItemProgressFill key={f.id} {...f} />)}
 *     </FileUpload.List>
 *   </FileUpload.Root>
 *
 * Also exports a simple <SimpleFileUpload /> for quick usage (backward-compat).
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  Upload, X, File as FileIcon, CheckCircle2, AlertCircle,
  RotateCcw, FileText, Image as ImageIcon, FileSpreadsheet,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  progress: number;
  failed?: boolean;
  error?: string;
}

// ─── Utilities ──────────────────────────────────────────────────────────────

export const getReadableFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

/** Returns a Lucide icon based on file extension */
const getFileIcon = (name: string) => {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
    return <ImageIcon className="h-4 w-4" />;
  }
  if (['pdf'].includes(ext)) {
    return <FileText className="h-4 w-4" />;
  }
  if (['csv', 'xlsx', 'xls', 'tsv'].includes(ext)) {
    return <FileSpreadsheet className="h-4 w-4" />;
  }
  return <FileIcon className="h-4 w-4" />;
};

// ─── Root ───────────────────────────────────────────────────────────────────

interface RootProps {
  children: React.ReactNode;
  className?: string;
}

const Root: React.FC<RootProps> = ({ children, className }) => (
  <div className={cn('space-y-3', className)}>{children}</div>
);

// ─── DropZone ───────────────────────────────────────────────────────────────

interface DropZoneProps {
  onDropFiles: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
  isDisabled?: boolean;
  label?: string;
  description?: string;
  maxSize?: number;
  className?: string;
}

const DropZone: React.FC<DropZoneProps> = ({
  onDropFiles,
  accept,
  multiple = true,
  isDisabled = false,
  label = 'Upload files',
  description,
  maxSize,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (!isDisabled && e.dataTransfer.files.length > 0) {
        onDropFiles(e.dataTransfer.files);
      }
    },
    [isDisabled, onDropFiles],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onDropFiles(e.target.files);
      e.target.value = ''; // reset so same file can be re-uploaded
    }
  };

  return (
    <div
      className={cn(
        'relative border-2 border-dashed p-6 text-center transition-all cursor-pointer group',
        isDragOver
          ? 'border-[var(--primary-color)] bg-[var(--primary-color)]/5'
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
        isDisabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
      style={{ borderRadius: 'var(--border-radius, 8px)' }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!isDisabled) setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
      onClick={() => !isDisabled && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="hidden"
        disabled={isDisabled}
      />

      <div className={cn(
        'mx-auto mb-3 w-10 h-10 rounded-full flex items-center justify-center transition-colors',
        isDragOver
          ? 'bg-[var(--primary-color)]/10 text-[var(--primary-color)]'
          : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 group-hover:text-slate-500',
      )}>
        <Upload className="h-5 w-5" />
      </div>

      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </p>
      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
        {description || `Drag & drop or click to browse${accept ? ` (${accept})` : ''}`}
      </p>
      {maxSize && (
        <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
          Max size: {getReadableFileSize(maxSize)}
        </p>
      )}
    </div>
  );
};

// ─── List ───────────────────────────────────────────────────────────────────

interface ListProps {
  children: React.ReactNode;
  className?: string;
}

const List: React.FC<ListProps> = ({ children, className }) => (
  <div className={cn('space-y-2', className)}>{children}</div>
);

// ─── ListItemProgressFill ───────────────────────────────────────────────────
// File item with a background fill progress bar — the primary display style.

interface ListItemProgressFillProps {
  id: string;
  name: string;
  size: number;
  type?: string;
  progress: number;
  failed?: boolean;
  error?: string;
  onDelete?: () => void;
  onRetry?: () => void;
  className?: string;
}

const ListItemProgressFill: React.FC<ListItemProgressFillProps> = ({
  name,
  size,
  progress,
  failed = false,
  error,
  onDelete,
  onRetry,
  className,
}) => {
  const isComplete = progress >= 100 && !failed;
  const isUploading = progress > 0 && progress < 100 && !failed;

  return (
    <div
      className={cn(
        'relative overflow-hidden border transition-all',
        failed
          ? 'border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/20'
          : isComplete
            ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'
            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900',
        className,
      )}
      style={{ borderRadius: 'var(--border-radius, 8px)' }}
    >
      {/* Progress fill background */}
      {isUploading && (
        <div
          className="absolute inset-0 bg-[var(--primary-color)]/5 transition-[width] duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      )}

      {/* Content */}
      <div className="relative flex items-center gap-3 px-3 py-2.5">
        {/* File icon */}
        <div
          className={cn(
            'flex items-center justify-center w-8 h-8 rounded-lg shrink-0',
            failed
              ? 'bg-red-100 text-red-500 dark:bg-red-900/30 dark:text-red-400'
              : isComplete
                ? 'bg-emerald-50 text-emerald-500 dark:bg-emerald-900/20 dark:text-emerald-400'
                : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
          )}
        >
          {failed ? (
            <AlertCircle className="h-4 w-4" />
          ) : isComplete ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            getFileIcon(name)
          )}
        </div>

        {/* Name + size */}
        <div className="flex-1 min-w-0">
          <p className={cn(
            'text-sm font-medium truncate',
            failed
              ? 'text-red-700 dark:text-red-300'
              : 'text-slate-700 dark:text-slate-200',
          )}>
            {name}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              {getReadableFileSize(size)}
            </span>
            {isUploading && (
              <span className="text-[11px] font-medium text-[var(--primary-color)]">
                {progress}%
              </span>
            )}
            {failed && error && (
              <span className="text-[11px] text-red-500 dark:text-red-400">
                {error}
              </span>
            )}
            {failed && !error && (
              <span className="text-[11px] text-red-500 dark:text-red-400">
                Upload failed
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {failed && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              title="Retry upload"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-950/30 text-slate-400 hover:text-red-500 transition-colors"
              title="Remove file"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar (thin bottom line) */}
      {isUploading && (
        <div className="h-0.5 bg-slate-100 dark:bg-slate-800">
          <div
            className="h-full bg-[var(--primary-color)] transition-[width] duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

// ─── ListItemSimple ─────────────────────────────────────────────────────────
// Compact file item without progress (for completed/static lists).

interface ListItemSimpleProps {
  name: string;
  size: number;
  status?: 'pending' | 'success' | 'error';
  error?: string;
  onDelete?: () => void;
  className?: string;
}

const ListItemSimple: React.FC<ListItemSimpleProps> = ({
  name,
  size,
  status = 'pending',
  error,
  onDelete,
  className,
}) => (
  <div
    className={cn(
      'flex items-center gap-2 px-3 py-2 border',
      status === 'error'
        ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/30 text-red-600'
        : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300',
      className,
    )}
    style={{ borderRadius: 'var(--border-radius, 8px)' }}
  >
    {status === 'error' ? (
      <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
    ) : status === 'success' ? (
      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
    ) : (
      getFileIcon(name)
    )}
    <span className="flex-1 truncate text-xs">{name}</span>
    <span className="text-[10px] text-slate-400 shrink-0">{getReadableFileSize(size)}</span>
    {error && <span className="text-[10px] text-red-500">{error}</span>}
    {onDelete && (
      <button
        type="button"
        onClick={onDelete}
        className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 transition-colors"
      >
        <X className="h-3 w-3" />
      </button>
    )}
  </div>
);

// ─── Compound export ────────────────────────────────────────────────────────

export const FileUpload = {
  Root,
  DropZone,
  List,
  ListItemProgressFill,
  ListItemSimple,
};

// ─── SimpleFileUpload (backward-compatible) ─────────────────────────────────

export interface SimpleFileUploadProps {
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number;
  onUpload?: (files: File[]) => void;
  onChange?: (files: File[]) => void;
  disabled?: boolean;
  label?: string;
  description?: string;
  className?: string;
}

export const SimpleFileUpload: React.FC<SimpleFileUploadProps> = ({
  accept,
  multiple = false,
  maxFiles = 10,
  maxSize,
  onUpload,
  onChange,
  disabled = false,
  label,
  description,
  className = '',
}) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);

  const handleDrop = useCallback(
    (fileList: FileList) => {
      const incoming = Array.from(fileList).slice(0, maxFiles - files.length);
      const validated: UploadedFile[] = incoming.map((file) => {
        const isTooLarge = maxSize && file.size > maxSize;
        return {
          id: `${file.name}-${Date.now()}-${Math.random()}`,
          name: file.name,
          size: file.size,
          type: file.type,
          progress: isTooLarge ? 0 : 100,
          failed: !!isTooLarge,
          error: isTooLarge ? `Exceeds ${getReadableFileSize(maxSize)} limit` : undefined,
        };
      });

      const updated = [...validated, ...files];
      setFiles(updated);

      const validFiles = incoming.filter((_, i) => !validated[i].failed);
      if (validFiles.length > 0) {
        onChange?.(validFiles);
        onUpload?.(validFiles);
      }
    },
    [files, maxFiles, maxSize, onChange, onUpload],
  );

  return (
    <FileUpload.Root className={className}>
      <FileUpload.DropZone
        onDropFiles={handleDrop}
        accept={accept}
        multiple={multiple}
        isDisabled={disabled}
        label={label}
        description={description}
        maxSize={maxSize}
      />
      {files.length > 0 && (
        <FileUpload.List>
          {files.map((f) => (
            <FileUpload.ListItemProgressFill
              key={f.id}
              {...f}
              onDelete={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))}
            />
          ))}
        </FileUpload.List>
      )}
    </FileUpload.Root>
  );
};

export default FileUpload;
